import { computed, reactive, Ref, ref, unref } from 'vue';
import { useDialog } from '@negar/core';
import {
  InitialPublicOfferItem,
  Instrument,
  OrderHistoryItem,
  OrderItem,
  OrderSideEnum,
  PortfolioItem,
} from '@libs/api-reference';
import { getSymbolIsin } from '@core/symbol';
import {
  isModifiedAndEditedState,
  LEGACY_ORDER_STATUSES_GROUPS,
  NEW_OMS_ORDER_STATUSES_GROUPS,
  ORDER_TYPES_LABEL,
  useGetOfflineOrderStatusQuery,
  isNewOms,
} from '@core/order';
import { dataLayer } from '@libs/gtm';

import {
  OrderFormDetail,
  OrderFormDialog,
  DeleteOrderDialogFooter,
  OfflineOrderDetail,
  MicroTransaction,
  ConditionalOrderDetail,
  OrderDetail,
  OrderPosition,
  OrderHistoryDialog,
  ConditionalDescription,
  ConditionalPriceDescription,
  OfflineOrderDescriptionDialog,
  OfflineOrderSubmissionDialog,
  OrderRiskAnnouncementDialog,
  OfflineOrderConfirmDialog,
  PetrochemicalsOrderRiskAnnouncementDialog,
} from '@/modules/order';

import { i18n } from '@/plugins/i18n';

const { t } = i18n.global;

export default function useOrderDialogs() {
  const { add: addDialog } = useDialog();

  const { isOffline } = useGetOfflineOrderStatusQuery();

  function openNewOrderFormDialog({
    type,
    symbol,
    isLinkToPrice = false,
    quantity = null,
    price = null,
    isUnderWriting = false,
  }: {
    type: string;
    isLinkToPrice?: boolean;
    symbol: Pick<Instrument, 'isin' | 'instrumentName'>;
    quantity?: number | null;
    price?: number | null;
    isUnderWriting?: boolean;
  }) {
    const model = reactive<{ quantity?: null | number; price?: null | number }>(
      { quantity, price }
    );
    const typeRef = ref(type);
    const dialogId = ref();

    const title = computed(
      () =>
        `${unref(typeRef) === 'sell' ? t('common.sell') : t('common.buy')} ${
          symbol?.instrumentName
        }`
    );

    dataLayer.push({
      event: 'orderType',
      type: isOffline.value
        ? ORDER_TYPES_LABEL.offline
        : ORDER_TYPES_LABEL.online,
    });

    dialogId.value = addDialog({
      title: title,
      expandableLabel: t('modules.order.symbol_detail'),
      expandable: true,
      defaultExpanded: true,
      multiple: true,
      content: computed(() => ({
        component: OrderFormDialog,
        props: {
          baseModel: model,
          type: unref(typeRef),
          isin: getSymbolIsin(symbol),
          isLinkToPrice,
          isEditOrderForm: false,
          isUnderWriting,
          dialogId: unref(dialogId),
          onTabChange: (newValue: string) => {
            typeRef.value = newValue;
          },
        },
      })),
      expandedContent: computed(() => ({
        component: OrderFormDetail,
        props: {
          isin: getSymbolIsin(symbol),
          onUpdateOrderForm: ({
            quantity,
            price,
          }: {
            quantity?: number;
            price?: number;
          }) => {
            if (price) {
              model.price = price;
            }

            if (quantity) {
              model.quantity = quantity;
            }
          },
        },
      })),
    });
  }

  function openEditOrderFormDialog({
    type,
    selectedOrder,
  }: {
    type: string;
    selectedOrder: OrderItem;
  }) {
    const model = reactive<{ quantity?: null | number; price?: null | number }>(
      {
        quantity: selectedOrder.quantity - selectedOrder.executedQuantity,
        price: selectedOrder.price,
      }
    );
    const dialogId = ref();

    const title = `${t('modules.order.edit')} ${
      type === 'sell' ? t('common.sell') : t('common.buy')
    } ${selectedOrder?.instrumentName}`;

    dialogId.value = addDialog({
      title: title,
      expandableLabel: t('modules.order.symbol_detail'),
      expandable: true,
      defaultExpanded: true,
      multiple: true,
      content: computed(() => ({
        component: OrderFormDialog,
        props: {
          baseModel: model,
          isin: getSymbolIsin(selectedOrder),
          type,
          isEditOrderForm: true,
          selectedOrder,
          dialogId: unref(dialogId),
        },
      })),
      expandedContent: computed(() => ({
        component: OrderFormDetail,
        props: {
          isin: getSymbolIsin(selectedOrder),
          onUpdateOrderForm: ({
            quantity,
            price,
          }: {
            quantity?: number;
            price?: number;
          }) => {
            if (price) {
              model.price = price;
            }

            if (quantity) {
              model.quantity = quantity;
            }
          },
        },
      })),
    });
  }

  function openDeleteOfflineOrderDialog({
    order,
    referenceElementRef,
    loading,
    handleDelete,
  }: {
    order: OrderItem;
    referenceElementRef: Ref<HTMLElement | undefined>;
    loading: boolean;
    handleDelete: (...args) => any;
  }) {
    addDialog({
      bodyClass: '!w-1/5',
      title: `${t('modules.order.cancelOrder')} ${
        order.orderSideId === OrderSideEnum.Buy
          ? t('common.buy')
          : t('common.sell')
      } ${order.instrumentName}`,
      content: t('modules.order.cancelOrderQuestion'),
      referenceElementRef,
      placement: 'top',
      footer: computed(() => ({
        component: DeleteOrderDialogFooter,
        props: {
          order,
          loadingDeleteOrder: loading,
          onDeleteOrder: handleDelete,
        },
      })),
    });
  }

  function openDeleteOrderDialog({
    order,
    handleDelete,
    referenceElementRef,
    loading,
    afterClose,
  }: {
    order: OrderItem;
    referenceElementRef: Ref<HTMLElement | undefined>;
    loading: boolean;
    handleDelete: (...args) => any;
    afterClose: (...args) => any;
  }) {
    const deleteOrderTitle = `${t('modules.order.cancelOrder')} ${
      order.orderSideId === OrderSideEnum.Buy
        ? t('common.buy')
        : t('common.sell')
    } ${order.instrumentName}`;

    addDialog({
      afterClose,
      bodyClass: '!w-1/5',
      title: deleteOrderTitle,
      referenceElementRef,
      placement: 'top',
      content: t('modules.order.cancelOrderQuestion', {
        instrumentName: order.instrumentName,
        orderSide:
          order.orderSideId === OrderSideEnum.Buy
            ? t('common.buy')
            : t('common.sell'),
      }),
      footer: computed(() => ({
        component: DeleteOrderDialogFooter,
        props: {
          order,
          loadingDeleteOrder: loading,
          onDeleteOrder: handleDelete,
        },
      })),
    });
  }

  function openOrderInfoDialog({
    order,
    offer,
    afterClose,
  }: {
    order: OrderItem | OrderHistoryItem;
    offer?: InitialPublicOfferItem;
    afterClose?: (...args) => any;
  }) {
    const isOrderDone =
      isModifiedAndEditedState(
        order.orderStateEnglishTitle,
        order.executedQuantity
      ) === 'Executed';

    const isOrderDetailExpanded = ref(isOrderDone);

    const orderInfoTitle = `${t('modules.order.orderInfo')} ${
      order?.orderSideId === OrderSideEnum.Buy
        ? t('common.buy')
        : t('common.sell')
    } ${order?.instrumentName}`;

    addDialog({
      afterClose,
      title: orderInfoTitle,
      content: computed(() => ({
        component: OrderDetail,
        props: {
          selectedOrder: order,
          offer,
        },
      })),
      expandable: isOrderDone,
      defaultExpanded: isOrderDetailExpanded.value,
      onExpandedUpdate: (newValue) => (isOrderDetailExpanded.value = newValue),
      expandableLabel: computed(() =>
        isOrderDetailExpanded.value
          ? `${t('misc.close')} ${t('modules.order.microTransactions')}`
          : t('modules.order.microTransactions')
      ),
      expandedContent: isOrderDone
        ? computed(() => ({
            component: MicroTransaction,
            props: {
              order,
              isLegacy: !isNewOms.value,
            },
          }))
        : undefined,
    });
  }

  function openOrderHistoryDialog({ order }: { order: OrderHistoryItem }) {
    const isOrderDone =
      isModifiedAndEditedState(
        order.orderStateEnglishTitle,
        order.executedQuantity
      ) === 'Executed';

    const isOrderDetailExpanded = ref(isOrderDone);
    const isLegacy = Boolean(order.legacyOrderState);

    addDialog({
      title: t('modules.order.orderInfo'),
      expandable: !isLegacy
        ? NEW_OMS_ORDER_STATUSES_GROUPS.done.includes(order.orderStateId)
        : LEGACY_ORDER_STATUSES_GROUPS.done.includes(order.orderStateId),
      defaultExpanded: isOrderDetailExpanded.value,
      content: {
        component: OrderHistoryDialog,
        props: {
          order,
        },
      },
      onExpandedUpdate: (newValue) => (isOrderDetailExpanded.value = newValue),
      expandableLabel: computed(() =>
        isOrderDetailExpanded.value
          ? `${t('misc.close')} ${t('modules.order.microTransactions')}`
          : t('modules.order.microTransactions')
      ),
      expandedContent: computed(() => ({
        component: MicroTransaction,
        props: {
          order,
          isLegacy,
        },
      })),
    });
  }

  function openConditionalOrderInfoDialog({
    order,
    afterClose,
  }: {
    order: OrderItem | OrderHistoryItem;
    afterClose?: (...args) => any;
  }) {
    const orderInfoTitle = `${t('modules.order.orderInfo')} ${
      order?.orderSideId === OrderSideEnum.Buy
        ? t('common.buy')
        : t('common.sell')
    } ${order?.instrumentName}`;

    addDialog({
      afterClose,
      title: orderInfoTitle,
      content: computed(() => ({
        component: ConditionalOrderDetail,
        props: {
          selectedOrder: order,
        },
      })),
    });
  }

  function openOfflineOrderInfoDialog({
    order,
    offer,
    afterClose,
  }: {
    order: OrderItem | OrderHistoryItem;
    offer?: InitialPublicOfferItem;
    afterClose?: (...args) => any;
  }) {
    const orderInfoTitle = `${t('modules.order.orderInfo')} ${
      order?.orderSideId === OrderSideEnum.Buy
        ? t('common.buy')
        : t('common.sell')
    } ${order?.instrumentName}`;

    addDialog({
      afterClose,
      title: orderInfoTitle,
      content: computed(() => ({
        component: OfflineOrderDetail,
        props: {
          selectedOrder: order,
          offer,
        },
      })),
    });
  }

  function openOrderPositionDialog({
    order,
    referenceElementRef,
  }: {
    order: OrderItem;
    referenceElementRef: Ref<HTMLElement | undefined>;
  }) {
    addDialog({
      title: t('modules.order.orderPositionTitle', {
        orderSide:
          order.orderSideId === OrderSideEnum.Buy
            ? t('common.buy')
            : t('common.sell'),
        instrumentName: order.instrumentName,
      }),
      multiple: true,
      referenceElementRef,
      placement: 'top-right',
      content: computed(() => ({
        component: OrderPosition,
        props: {
          order,
        },
      })),
    });
  }

  function openConditionalOrderDescriptionDialog() {
    addDialog({
      title: t('orders.conditional.conditionalTradingُ'),
      bodyClass: '!w-[375px]',
      content: computed(() => ({
        component: ConditionalDescription,
        props: {},
      })),
    });
  }

  function openConditionalOrderPriceDescriptionDialog() {
    addDialog({
      title: t('orders.conditional.conditionalTradingُ'),
      bodyClass: '!w-[375px]',
      content: computed(() => ({
        component: ConditionalPriceDescription,
        props: {},
      })),
    });
  }

  function openOfflineOrderDescriptionDialog() {
    addDialog({
      title: `${t('orders.trade')} ${t('orders.twentyFourHour')}`,
      expandable: false,
      multiple: false,
      bodyClass: '!w-1/4',
      content: computed(() => ({
        component: OfflineOrderDescriptionDialog,
        props: {},
      })),
    });
  }

  function openOfflineOrderSubmissionDialog({
    formModel,
    isin,
    type,
  }: {
    formModel: {
      quantity?: number | null;
      price?: number | null;
      totalValue?: number | null;
      validityDate?: Date;
    };
    isin: string;
    type: 'buy' | 'sell';
  }) {
    addDialog({
      bodyClass: '!w-[375px]',
      title: t('orders.twentyFourHour'),
      multiple: false,
      content: computed(() => ({
        component: OfflineOrderSubmissionDialog,
        props: {
          formModel,
          isin,
          type,
        },
      })),
    });
  }

  function openAlgorithmOrderRiskDescriptionDialog(
    orderType: 'conditional' | 'offline'
  ) {
    addDialog({
      bodyClass: '!w-1/4',
      title: t('orders.riskAnnouncement.algorithmTitle'),
      multiple: false,
      content: computed(() => ({
        component: OrderRiskAnnouncementDialog,
        props: { orderType },
      })),
    });
  }

  function openPetrochemicalsOrderRiskDescriptionDialog() {
    addDialog({
      bodyClass: '!w-1/4',
      title: t('orders.symbolUnderCautionTitle'),
      multiple: false,
      content: computed(() => ({
        component: PetrochemicalsOrderRiskAnnouncementDialog,
        props: {},
      })),
    });
  }

  function openOfflineConfirmDialog({
    instrumentName,
    description = '',
    type,
    createOfflineOrder,
  }: {
    instrumentName: string | null;
    type: 'buy' | 'sell';
    description?: string;
    createOfflineOrder: (...args) => any;
  }) {
    addDialog({
      title: t('common.sendConfirmation'),
      content: computed(() => ({
        component: OfflineOrderConfirmDialog,
        props: {
          instrumentName,
          type,
          description,
          onConfirmOrder: createOfflineOrder,
        },
      })),
    });
  }

  return {
    openNewOrderFormDialog,
    openEditOrderFormDialog,
    openDeleteOrderDialog,
    openOfflineOrderInfoDialog,
    openOrderInfoDialog,
    openOrderPositionDialog,
    openOrderHistoryDialog,
    openConditionalOrderInfoDialog,
    openDeleteOfflineOrderDialog,
    openConditionalOrderPriceDescriptionDialog,
    openConditionalOrderDescriptionDialog,
    openOfflineOrderDescriptionDialog,
    openOfflineOrderSubmissionDialog,
    openAlgorithmOrderRiskDescriptionDialog,
    openOfflineConfirmDialog,
    openPetrochemicalsOrderRiskDescriptionDialog,
  };
}
